import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { gqlProfileUnionFilter, gqlRole } from '@minecraft.graphql-types';
import { SelectOption } from '@minecraft.typings';
import { useFeatureExperiment, FEATURE_FLAGS } from '@blocs.features-experiments';
import {
  QUERY_PROFILE_SEARCH_UNION_FILTER_BY_ROLE,
  QUERY_ROLE_DETAILS,
} from '../../FilterBasic/FilterBasicCollection/FilterBasicCollectionGraphQL';
import { sagAftraOption } from '../config';
import { getEthnicAppearance, getPlayableAgeRange, getWorkingLocation } from '../mappers';
import { FiltersNameEnum, RangeEnum } from '../types';
import { useGenderAppearance } from './useGenderAppearance';
import { useProfileUnionFilter } from './useProfileUnionFilter';

export interface AutofillRoleFiltersBase {
  [FiltersNameEnum.autofillByRoleCriteria]: boolean;
  [FiltersNameEnum.stageName]: string;
  [FiltersNameEnum.unionStatus]: SelectOption[];
  [FiltersNameEnum.excludeUnionStatus]: SelectOption[];
  [FiltersNameEnum.gender]: SelectOption[];
  [FiltersNameEnum.additionallyIdentifiesAs]: SelectOption[];
  [FiltersNameEnum.ethnicAppearance]: SelectOption[];
  [FiltersNameEnum.playableAgeRange]: {
    [FiltersNameEnum.switchTo]: boolean;
    [FiltersNameEnum.min]: number;
    [FiltersNameEnum.max]: number;
    type: RangeEnum.age;
  };
  [FiltersNameEnum.workingLocation]: SelectOption[];
  [FiltersNameEnum.primaryWorkingLocation]: SelectOption[];
}

export interface UseRoleFiltersParams<FiltersValues = AutofillRoleFiltersBase> {
  roleId?: number;
  initialFilters?: FiltersValues;
  getAdditionalAutofillFilters?: () => Partial<FiltersValues>;
  roleProp?: gqlRole;
}

export function useRoleFilters<FilterValues = AutofillRoleFiltersBase>({
  initialFilters,
  roleId,
  getAdditionalAutofillFilters,
  roleProp,
}: UseRoleFiltersParams<FilterValues>) {
  const getGenderAppearance = useGenderAppearance();
  const getProfileUnionFilter = useProfileUnionFilter();

  const { data: dataProfileSearchUnionFilterByRole, error: filterRoleUnionError } = useQuery<{
    getProfileSearchUnionFilterByRole: gqlProfileUnionFilter[];
  }>(QUERY_PROFILE_SEARCH_UNION_FILTER_BY_ROLE, {
    variables: {
      roleId,
    },
    skip: !roleId,
  });

  const { data: dataRoleDetails, error: filterRoleError } = useQuery<{ roles: gqlRole[] }>(QUERY_ROLE_DETAILS, {
    variables: {
      roleId,
      withGenderAppearance: true,
      withEthnicAppearance: true,
      withPlayableAgeRange: true,
      withWorkingLocation: true,
      skipProjectReleaseLocations: true,
    },
    skip: !roleId || Boolean(roleProp),
  });

  const profileSearchUnionFilterByRole = useMemo(() => {
    return dataProfileSearchUnionFilterByRole?.getProfileSearchUnionFilterByRole?.[0] || ({} as gqlProfileUnionFilter);
  }, [dataProfileSearchUnionFilterByRole]);

  const isPrimaryWorkingLocationEnabled = useFeatureExperiment(FEATURE_FLAGS.WEB_CASTING_PRIMARY_WORKING_FILTER);

  const role = useMemo(() => {
    // use the role prop if it is passed
    if (roleProp) return roleProp;
    // otherwise use data from the details query
    return dataRoleDetails?.roles[0] || ({} as gqlRole);
  }, [roleProp, dataRoleDetails]);
  const error = filterRoleUnionError || filterRoleError;

  const filterResult = useMemo(() => {
    if (!role) return initialFilters;

    let filtersByRoleCriteria: FilterValues = { ...initialFilters, [FiltersNameEnum.autofillByRoleCriteria]: true };

    const union = getProfileUnionFilter({
      unionStatusName: FiltersNameEnum.unionStatus,
      excludeUnionStatusName: FiltersNameEnum.excludeUnionStatus,
      profileSearchUnionFilterByRole,
    });

    if (role?.displayedRoleUnion !== sagAftraOption.label) {
      filtersByRoleCriteria[FiltersNameEnum.unionStatus] = union[FiltersNameEnum.unionStatus];
      filtersByRoleCriteria[FiltersNameEnum.excludeUnionStatus] = union[FiltersNameEnum.excludeUnionStatus];
    }

    filtersByRoleCriteria[FiltersNameEnum.gender] = getGenderAppearance({
      genderName: FiltersNameEnum.gender,
      additionallyIdentifiesAsName: FiltersNameEnum.additionallyIdentifiesAs,
      roleDetails: role,
    });

    filtersByRoleCriteria[FiltersNameEnum.ethnicAppearance] = getEthnicAppearance({
      roleDetails: role,
    });

    const playableAgeRange = getPlayableAgeRange({
      switchToName: FiltersNameEnum.switchTo,
      ageMinName: FiltersNameEnum.min,
      ageMaxName: FiltersNameEnum.max,
      roleDetails: role,
    });

    filtersByRoleCriteria[FiltersNameEnum.playableAgeRange] = {
      ...filtersByRoleCriteria[FiltersNameEnum.playableAgeRange],
      ...playableAgeRange,
    };

    const locationFiltersEnum = isPrimaryWorkingLocationEnabled
      ? FiltersNameEnum.primaryWorkingLocation
      : FiltersNameEnum.workingLocation;

    filtersByRoleCriteria[locationFiltersEnum] = getWorkingLocation({
      roleDetails: role,
    });

    if (typeof getAdditionalAutofillFilters === 'function') {
      filtersByRoleCriteria = {
        ...filtersByRoleCriteria,
        ...getAdditionalAutofillFilters(),
      };
    }

    return filtersByRoleCriteria;
  }, [
    getAdditionalAutofillFilters,
    getGenderAppearance,
    getProfileUnionFilter,
    initialFilters,
    profileSearchUnionFilterByRole,
    role,
    isPrimaryWorkingLocationEnabled,
  ]);

  return useMemo(() => {
    return {
      filterResult,
      error,
    };
  }, [filterResult, error]);
}
